import type {
  StormcloudVideoPlayerConfig,
  Scte35Marker,
  AdController,
  AdBreak,
} from "../types";
import { createHlsAdPlayer } from "../sdk/hlsAdPlayer";
import { createPalNonceManager } from "../sdk/pal";
import type { PalNonceManager } from "../sdk/pal";
import {
  sendAdDetectTracking,
} from "../utils/tracking";
import { initializePolyfills } from "../utils/polyfills";
import { fetchConsentSignals } from "../utils/vastMacros";
import {
  logBrowserInfo,
  getBrowserConfigOverrides,
} from "../utils/browserCompat";

import type { Scte35CueContext } from "./playerTypes";
import { isManifestBasedMarker } from "./Scte35Parser";
import { Scte35CueManager } from "./Scte35CueManager";
import { AdConfigManager } from "./AdConfigManager";
import { AdTimingService } from "./AdTimingService";
import { AdPreloadPool } from "./AdPreloadPool";
import { PlaceholderLayer } from "./PlaceholderLayer";
import { HlsEngine } from "./HlsEngine";
import { AdBreakOrchestrator } from "./AdBreakOrchestrator";
import {
  toggleMute as ctrlToggleMute,
  toggleFullscreen as ctrlToggleFullscreen,
  isMuted as ctrlIsMuted,
  setMuted as ctrlSetMuted,
  setVolume as ctrlSetVolume,
  getVolume as ctrlGetVolume,
  isFullscreen as ctrlIsFullscreen,
  resizePlayer as ctrlResizePlayer,
} from "./PlayerControls";

export class StormcloudVideoPlayer {
  private readonly video: HTMLVideoElement;
  private readonly config: StormcloudVideoPlayerConfig;
  private adPlayer: AdController;
  private attached = false;

  private timeUpdateHandler?: (event: Event) => void;
  private endedHandler?: (event: Event) => void;
  private emptiedHandler?: (event: Event) => void;

  private palNonce: PalNonceManager;
  private palPlaybackStarted: boolean = false;

  private readonly cueManager: Scte35CueManager;
  private readonly adConfig: AdConfigManager;
  private readonly timing: AdTimingService;
  private readonly preloadPool: AdPreloadPool;
  private readonly placeholder: PlaceholderLayer;
  private readonly hlsEngine: HlsEngine;
  private readonly adBreak: AdBreakOrchestrator;

  constructor(config: StormcloudVideoPlayerConfig) {
    initializePolyfills();

    const browserOverrides = getBrowserConfigOverrides();

    this.config = { ...browserOverrides, ...config };
    this.video = config.videoElement;

    logBrowserInfo(config.debugAdTiming);

    this.adPlayer = this.createAdPlayer(false);
    this.palNonce = createPalNonceManager();

    this.cueManager = new Scte35CueManager(!!this.config.debugAdTiming, {
      onEarlyStartCue: (marker, fragmentSn, cueKey) => {
        this.startAdPrefetch(marker, fragmentSn, cueKey);
      },
      onPlaybackCue: (marker, cueKey) => {
        this.onScte35Marker(marker, cueKey);
      },
      isInAdBreak: () => this.adBreak.inAdBreak,
    });

    this.adConfig = new AdConfigManager(this.config, this.video);

    this.timing = new AdTimingService(this.config, this.video, {
      onAdStopTimerFired: () => this.onAdStopTimerFired(),
      onAdStartTimerFired: (marker, cueKey) => {
        this.startScte35AdBreak(
          marker,
          cueKey,
          marker.durationSeconds != null ? marker.durationSeconds * 1000 : this.timing.expectedAdBreakDurationMs
        );
      },
      onWatchdogTimeout: () => this.adBreak.handleAdFailure(),
      onFailsafeTimeout: () => this.adBreak.handleAdFailure(),
    });

    this.preloadPool = new AdPreloadPool({
      debug: !!this.config.debugAdTiming,
      createAdPlayer: (c) => this.createAdPlayer(c),
      shouldContinueLiveStreamDuringAds: () => this.shouldContinueLiveStreamDuringAds(),
      timing: this.timing,
      generateVastUrlsWithCorrelators: (base, count) => this.generateVastUrls(base, count),
      isInAdBreak: () => this.adBreak.inAdBreak,
      getPendingAdBreak: () => this.adBreak.pendingAdBreak,
    });

    this.placeholder = new PlaceholderLayer(this.video, !!this.config.debugAdTiming);

    this.hlsEngine = new HlsEngine(this.config, this.video, this.cueManager, this.timing, {
      onManifestParsed: async (isLive) => this.onManifestParsed(isLive),
      onScte35Cue: (marker, context) => this.cueManager.onScte35Cue(marker, context),
      activatePendingCuesForFragment: (frag) => this.cueManager.activatePendingScte35CuesForFragment(frag),
      onFatalDestroy: () => this.destroy(),
      isInAdBreak: () => this.adBreak.inAdBreak,
      hasPendingAdBreak: () => this.adBreak.pendingAdBreak !== null,
    });

    this.adBreak = new AdBreakOrchestrator(
      {
        config: this.config,
        video: this.video,
        getAdPlayer: () => this.adPlayer,
        setAdPlayer: (p) => { this.adPlayer = p; },
        createAdPlayer: (c) => this.createAdPlayer(c),
        shouldContinueLiveStreamDuringAds: () => this.shouldContinueLiveStreamDuringAds(),
        getLiveSyncPosition: () => this.hlsEngine.getLiveSyncPosition(),
        generateVastUrls: (base, count) => this.generateVastUrls(base, count),
      },
      this.timing,
      this.preloadPool,
      this.adConfig,
      this.cueManager,
      this.placeholder
    );
  }

  private get debug(): boolean {
    return !!this.config.debugAdTiming;
  }

  private createAdPlayer(continueLiveStreamDuringAds: boolean): AdController {
    if (this.debug) {
      console.log("[StormcloudVideoPlayer] Creating HLS ad player");
    }
    return createHlsAdPlayer(this.video, {
      continueLiveStreamDuringAds,
      ...(this.config.licenseKey
        ? { licenseKey: this.config.licenseKey }
        : {}),
      ...(this.hlsEngine?.hls ? { mainHlsInstance: this.hlsEngine.hls } : {}),
    });
  }

  private generateVastUrls(baseUrl: string, count: number): string[] {
    return this.adConfig.generateVastUrlsWithCorrelators(
      baseUrl, count, this.adPlayer, this.palNonce, this.adBreak.inAdBreak
    );
  }

  // ───────────────────────────────────────────────────────────────
  //  Lifecycle
  // ───────────────────────────────────────────────────────────────

  async load(): Promise<void> {
    if (!this.attached) {
      this.attach();
    }

    try {
      await this.adConfig.fetchAdConfiguration();
    } catch (error) {
      if (this.debug) {
        console.warn(
          "[StormcloudVideoPlayer] Failed to fetch ad configuration:",
          error
        );
      }
    }

    this.palNonce.generateNonce({
      videoWidth: this.video.offsetWidth || this.video.clientWidth || 640,
      videoHeight: this.video.offsetHeight || this.video.clientHeight || 480,
      adWillAutoPlay: !!this.config.autoplay,
      adWillPlayMuted: true,
      playerVolume: 0,
      continuousPlayback: this.config.lowLatencyMode ?? false,
    }).catch(() => {});

    fetchConsentSignals()
      .then((signals) => {
        this.adConfig.consentSignals = signals;
      })
      .catch(() => {});

    this.adConfig.initializeTracking();

    if (this.hlsEngine.shouldUseNativeHls(() => this.getStreamType())) {
      await this.hlsEngine.setupNativeHls();

      const continueLiveStreamDuringAds = this.shouldContinueLiveStreamDuringAds();
      if (this.debug) {
        console.log("[StormcloudVideoPlayer] Native HLS ad behavior:", {
          isLive: this.hlsEngine.isLiveStream,
          continueLiveStreamDuringAds,
        });
      }

      this.adPlayer.destroy();
      this.adPlayer = this.createAdPlayer(continueLiveStreamDuringAds);
      this.adPlayer.initialize();
      return;
    }

    this.hlsEngine.setupHls();
  }

  private async onManifestParsed(isLive: boolean): Promise<void> {
    if (
      !this.adConfig.isVmapEnabled() &&
      !isLive &&
      this.adConfig.vmapBreaks.length === 0 &&
      this.adConfig.apiVastTagUrl
    ) {
      const prerollKey = "synthetic-vod-preroll";
      if (!this.adConfig.consumedVmapBreakIds.has(prerollKey)) {
        this.adConfig.vmapBreaks = [
          { id: prerollKey, startTimeMs: 0, vastTagUrl: this.adConfig.apiVastTagUrl },
        ];
        if (this.debug) {
          console.log(
            "[StormcloudVideoPlayer] Injected synthetic VOD preroll from apiVastTagUrl"
          );
        }
      }
    }

    if (this.debug) {
      const adBehavior = this.shouldContinueLiveStreamDuringAds()
        ? "live (main video continues muted during ads)"
        : "vod (main video pauses during ads)";
      console.log("[StormcloudVideoPlayer] Stream type detected:", {
        isLive,
        allowNativeHls: this.config.allowNativeHls,
        adBehavior,
      });
    }

    this.adPlayer.destroy();
    this.adPlayer = this.createAdPlayer(this.shouldContinueLiveStreamDuringAds());
    this.adPlayer.initialize();
  }

  private attach(): void {
    if (this.attached) return;
    this.attached = true;
    this.video.autoplay = !!this.config.autoplay;
    this.video.muted = !!this.config.muted;

    this.adPlayer.initialize();
    this.adPlayer.updateOriginalMutedState(this.video.muted, this.video.volume);
    this.adBreak.attachImaEventListeners();

    this.video.addEventListener("play", () => {
      if (!this.palPlaybackStarted && !this.adPlayer.isAdPlaying()) {
        this.palPlaybackStarted = true;
        this.palNonce.sendPlaybackStart();
      }
    });

    this.endedHandler = () => {
      if (this.palPlaybackStarted) {
        this.palPlaybackStarted = false;
        this.palNonce.sendPlaybackEnd();
      }
      this.onVideoEnded();
    };
    this.video.addEventListener("ended", this.endedHandler);

    this.video.addEventListener("mousedown", (e) => {
      this.palNonce.sendAdTouch(e as MouseEvent);
    });

    this.video.addEventListener("touchstart", (e) => {
      this.palNonce.sendAdTouch(e as TouchEvent);
    });

    this.timeUpdateHandler = () => {
      this.onTimeUpdate(this.video.currentTime);
    };
    this.video.addEventListener("timeupdate", this.timeUpdateHandler);

    this.emptiedHandler = () => {
      if (
        this.hlsEngine.nativeHlsMode &&
        this.hlsEngine.videoSrcProtection &&
        !this.adPlayer.isAdPlaying()
      ) {
        if (this.debug) {
          console.log(
            "[StormcloudVideoPlayer] Video src was cleared, restoring:",
            this.hlsEngine.videoSrcProtection
          );
        }
        const currentTime = this.video.currentTime;
        const wasPaused = this.video.paused;
        this.video.src = this.hlsEngine.videoSrcProtection;
        this.video.currentTime = currentTime;
        if (!wasPaused) {
          this.video.play().catch(() => {});
        }
      }
    };
    this.video.addEventListener("emptied", this.emptiedHandler);
  }

  private shouldContinueLiveStreamDuringAds(): boolean {
    // Continue content under ads for live streams regardless of native/hls.js
    // playback mode. Pausing live content during ads causes replayed break
    // filler after ads complete on CTV devices.
    if (this.hlsEngine.isLiveStream) {
      return true;
    }

    // Native HLS can still represent live as Infinity duration even when
    // explicit isLiveStream is not provided.
    const duration = this.video.duration;
    if (!Number.isFinite(duration) || duration === Infinity) {
      return true;
    }

    return false;
  }

  // ───────────────────────────────────────────────────────────────
  //  SCTE-35 marker orchestration
  // ───────────────────────────────────────────────────────────────

  private onScte35Marker(marker: Scte35Marker, cueKey?: string): void {
    if (this.debug) {
      console.log("[StormcloudVideoPlayer] SCTE-35 marker detected:", {
        type: marker.type,
        ptsSeconds: marker.ptsSeconds,
        durationSeconds: marker.durationSeconds,
        currentTime: this.video.currentTime,
        raw: marker.raw,
        hasPendingAdBreak: !!this.adBreak.pendingAdBreak,
      });
    }

    if (marker.type === "start") {
      if (this.adBreak.inAdBreak) {
        if (marker.durationSeconds != null) {
          const newDurationMs = marker.durationSeconds * 1000;
          if (this.timing.expectedAdBreakDurationMs == null || newDurationMs > this.timing.expectedAdBreakDurationMs) {
            this.timing.setAdBreakDurationBoundary(newDurationMs);
            this.timing.scheduleAdStopAtBreakBoundary();
            if (this.debug) {
              console.log(
                `[StormcloudVideoPlayer] Updated ad break duration from subsequent marker: ${newDurationMs}ms, remaining: ${this.timing.getWallClockRemainingAdMs()}ms`
              );
            }
          }
        }
        return;
      }

      if (!this.adBreak.adDetectSentForCurrentBreak) {
        this.adBreak.adDetectSentForCurrentBreak = true;
        const detectPayload: {
          durationSeconds?: number;
          ptsSeconds?: number;
          detectedAtFragmentSn?: number;
        } = {};
        if (marker.durationSeconds != null) detectPayload.durationSeconds = marker.durationSeconds;
        if (marker.ptsSeconds != null) detectPayload.ptsSeconds = marker.ptsSeconds;
        sendAdDetectTracking(this.config.licenseKey, { source: "scte35", timestamp: new Date().toISOString(), ...detectPayload }).catch(() => {});
      }

      const hasPrefetchedAds = this.adBreak.pendingAdBreak && this.adBreak.pendingAdBreak.vastUrls.length > 0;

      const durationMs =
        marker.durationSeconds != null
          ? marker.durationSeconds * 1000
          : (this.adBreak.pendingAdBreak?.marker.durationSeconds != null
              ? this.adBreak.pendingAdBreak.marker.durationSeconds * 1000
              : undefined);

      const isManifest = isManifestBasedMarker(marker);
      const forceImmediate = this.config.immediateManifestAds ?? true;

      if (this.debug) {
        console.log("[StormcloudVideoPlayer] Ad start decision:", {
          isManifestMarker: isManifest,
          forceImmediate,
          hasPts: typeof marker.ptsSeconds === "number",
          hasPrefetchedAds,
        });
      }

      if (isManifest && forceImmediate) {
        if (this.debug) {
          console.log(
            `[StormcloudVideoPlayer] Starting ad immediately (manifest-based)${hasPrefetchedAds ? " with prefetched ads" : ""}`
          );
        }
        this.timing.clearAdStartTimer();
        this.startScte35AdBreak(marker, cueKey, durationMs);
      } else if (typeof marker.ptsSeconds === "number") {
        const tol = this.config.driftToleranceMs ?? 1000;
        const nowMs = this.video.currentTime * 1000;
        const estCurrentPtsMs = nowMs - this.timing.ptsDriftEmaMs;
        const deltaMs = Math.floor(marker.ptsSeconds * 1000 - estCurrentPtsMs);

        if (this.debug) {
          console.log("[StormcloudVideoPlayer] PTS-based timing calculation:", {
            nowMs,
            estCurrentPtsMs,
            markerPtsMs: marker.ptsSeconds * 1000,
            deltaMs,
            tolerance: tol,
          });
        }

        if (deltaMs > tol) {
          if (this.debug) {
            console.log(`[StormcloudVideoPlayer] Scheduling ad start in ${deltaMs}ms`);
          }
          this.timing.expectedAdBreakDurationMs = durationMs;
          this.cueManager.scheduledScte35BreakKey = cueKey;
          this.timing.scheduleAdStartIn(deltaMs, marker, cueKey);
        } else {
          if (this.debug) {
            console.log("[StormcloudVideoPlayer] Starting ad immediately (within tolerance)");
          }
          this.timing.clearAdStartTimer();
          this.startScte35AdBreak(marker, cueKey, durationMs);
        }
      } else {
        if (this.debug) {
          console.log("[StormcloudVideoPlayer] Starting ad immediately (fallback)");
        }
        this.timing.clearAdStartTimer();
        this.startScte35AdBreak(marker, cueKey, durationMs);
      }
      return;
    }
    if (marker.type === "progress" && this.adBreak.inAdBreak) {
      if (marker.durationSeconds != null) {
        this.timing.setAdBreakDurationBoundary(marker.durationSeconds * 1000);
      }
      this.timing.scheduleAdStopAtBreakBoundary();

      if (
        !this.adPlayer.isAdPlaying() &&
        this.timing.activeAdRequestToken === null &&
        this.adBreak.adRequestQueue.length > 0 &&
        this.timing.hasTimeToStartAnotherAd()
      ) {
        this.adBreak.tryNextAvailableAdWithRateLimit();
      }
      return;
    }
    if (marker.type === "end") {
      if (!this.video.muted) {
        this.video.muted = true;
        if (this.debug) {
          console.log("[StormcloudVideoPlayer] Muted video on SCTE end marker");
        }
      }

      const remaining = this.timing.getRemainingAdMs();
      const adPlaying = this.adPlayer.isAdPlaying();
      const hasQueuedAds = this.adBreak.adRequestQueue.length > 0;

      if (this.debug) {
        console.log("[StormcloudVideoPlayer] SCTE-35 end marker received:", {
          remaining,
          adPlaying,
          hasQueuedAds,
          activeAdRequest: this.timing.activeAdRequestToken !== null,
        });
      }

      this.timing.scteAdBreakEndWallClockMs = Date.now();
      this.timing.clearAdStartTimer();
      this.timing.clearAdStopTimer();

      if (adPlaying) {
        this.adPlayer.stop().catch(() => {});
      }

      this.adBreak.handleAdPodComplete();
      return;
    }
  }

  private startScte35AdBreak(
    marker: Scte35Marker,
    cueKey: string | undefined,
    durationMs: number | undefined
  ): void {
    this.adBreak.inAdBreak = true;
    this.cueManager.activeScte35BreakKey = cueKey ?? this.cueManager.pendingScte35CueKey;
    this.cueManager.scheduledScte35BreakKey = undefined;
    this.timing.currentAdBreakStartWallClockMs =
      this.timing.resolveScteBreakStartWallClockMs(marker);
    this.timing.setAdBreakDurationBoundary(durationMs);

    this.adBreak.handleAdStart(marker);

    this.timing.scheduleAdStopAtBreakBoundary();
  }

  private onAdStopTimerFired(): void {
    if (!this.adBreak.inAdBreak) return;

    const adPlaying = this.adPlayer.isAdPlaying();
    const checkIntervalMs = Math.max(
      250,
      Math.floor(this.config.adBreakCheckIntervalMs ?? 1000)
    );
    const maxExtensionMsConfig = this.config.maxAdBreakExtensionMs;
    const maxExtensionMs =
      typeof maxExtensionMsConfig === "number" && maxExtensionMsConfig > 0
        ? maxExtensionMsConfig
        : 0;

    const endWallClockMs = this.timing.getAdBreakEndWallClockMs() ?? Date.now();
    const overrunMs = Math.max(0, Date.now() - endWallClockMs);

    const shouldExtendAdBreak = adPlaying && overrunMs < maxExtensionMs;

    if (shouldExtendAdBreak) {
      this.timing.scheduleAdStopCountdown(checkIntervalMs);
      return;
    }

    if (this.debug) {
      console.log(
        `[StormcloudVideoPlayer] Ad break boundary reached (overrun=${overrunMs}ms) - hard-stopping ads and resuming stream`
      );
    }

    if (adPlaying) {
      this.adPlayer.stop().catch(() => {});
    }

    this.adBreak.handleAdPodComplete();
  }

  // ───────────────────────────────────────────────────────────────
  //  Ad prefetch
  // ───────────────────────────────────────────────────────────────

  private startAdPrefetch(
    marker: Scte35Marker,
    fragmentSn?: number,
    cueKey?: string
  ): void {
    if (this.adBreak.pendingAdBreak || this.adBreak.inAdBreak) return;

    const scheduled = this.adConfig.findCurrentOrNextBreak(this.video.currentTime * 1000);
    const tags = this.adConfig.selectVastTagsForBreak(scheduled);

    let baseVastUrl: string;
    if (tags && tags.length > 0 && tags[0]) {
      baseVastUrl = tags[0];
    } else if (this.adConfig.apiVastTagUrl) {
      baseVastUrl = this.adConfig.apiVastTagUrl;
    } else {
      if (this.debug) console.warn("[StormcloudVideoPlayer] No VAST URL available for prefetch");
      return;
    }

    this.adConfig.beginNewAdPod();
    this.adConfig.podAssignedByPrefetch = true;

    const generatedUrls = this.generateVastUrls(baseVastUrl, 1);

    this.adBreak.pendingAdBreak = {
      marker,
      ...(fragmentSn !== undefined ? { detectedAtFragmentSn: fragmentSn } : {}),
      vastUrls: generatedUrls,
      isFetching: false,
      fetchStartTime: Date.now(),
    };
    this.cueManager.pendingScte35CueKey = cueKey;

    this.adBreak.adDetectSentForCurrentBreak = true;
    const detectPayload: {
      durationSeconds?: number;
      ptsSeconds?: number;
      detectedAtFragmentSn?: number;
    } = {};
    if (marker.durationSeconds != null) detectPayload.durationSeconds = marker.durationSeconds;
    if (marker.ptsSeconds != null) detectPayload.ptsSeconds = marker.ptsSeconds;
    if (fragmentSn !== undefined) detectPayload.detectedAtFragmentSn = fragmentSn;
    sendAdDetectTracking(this.config.licenseKey, { source: "scte35", timestamp: new Date().toISOString(), ...detectPayload }).catch(() => {});

    if (this.debug) {
      console.log("[PREFETCH] Starting ad prefetch for upcoming ad break");
      console.log(`[PREFETCH] Pre-generated ${generatedUrls.length} VAST URLs`);
    }

    this.preloadPool.startPreloadPool(baseVastUrl, generatedUrls.slice(0, 1), false);
  }

  // ───────────────────────────────────────────────────────────────
  //  VMAP time-based ad breaks
  // ───────────────────────────────────────────────────────────────

  private onTimeUpdate(currentTimeSec: number): void {
    if (!this.adConfig.isVmapEnabled() || this.adConfig.vmapBreaks.length === 0) return;
    if (this.adPlayer.isAdPlaying() || this.adBreak.inAdBreak) return;
    const nowMs = currentTimeSec * 1000;
    const breakToPlay = this.adConfig.findBreakForTime(nowMs);
    if (breakToPlay) {
      void this.handleVmapAdBreak(breakToPlay, nowMs).catch((error) => {
        if (this.debug) {
          console.warn("[StormcloudVideoPlayer] VMAP ad break failed gracefully:", error);
        }
      });
    }
  }

  private onVideoEnded(): void {
    if (!this.adConfig.isVmapEnabled() || this.adConfig.vmapBreaks.length === 0) return;
    if (this.adPlayer.isAdPlaying() || this.adBreak.inAdBreak) return;

    const durationMs = Number.isFinite(this.video.duration)
      ? Math.floor(this.video.duration * 1000)
      : 0;
    const postroll = this.adConfig.vmapBreaks.find(
      (b) =>
        b.startTimeMs === -1 &&
        !this.adConfig.consumedVmapBreakIds.has(this.adConfig.getAdBreakKey(b))
    );

    if (postroll) {
      void this.handleVmapAdBreak(postroll, durationMs).catch((error) => {
        if (this.debug) {
          console.warn("[StormcloudVideoPlayer] VMAP post-roll failed gracefully:", error);
        }
      });
    }
  }

  private async handleVmapAdBreak(
    adBreak: AdBreak,
    nowMs: number
  ): Promise<void> {
    const key = this.adConfig.getAdBreakKey(adBreak);
    if (this.adConfig.consumedVmapBreakIds.has(key)) return;

    const breakStartMs = this.adConfig.resolveBreakStartMs(adBreak);
    if (breakStartMs == null) return;

    const durationMs = adBreak.durationMs ?? 0;
    const endMs = breakStartMs + durationMs;
    const inWindow =
      durationMs > 0
        ? nowMs >= breakStartMs && nowMs < endMs
        : nowMs >= breakStartMs;

    if (inWindow) {
      this.adConfig.consumedVmapBreakIds.add(key);
      const tags = this.adConfig.selectVastTagsForBreak(adBreak);
      if (!tags || tags.length === 0) return;

      const first = tags[0] as string;
      const rest = tags.slice(1);
      this.adBreak.adPodQueue = rest;

      this.adPlayer.updateOriginalMutedState(this.video.muted, this.video.volume);
      this.adBreak.showAds = true;
      this.adBreak.inAdBreak = true;
      this.timing.currentAdBreakStartWallClockMs = Date.now();

      try {
        await this.adBreak.playSingleAd(first);
      } catch (error) {
        this.adBreak.adPodQueue = [];
        this.adBreak.inAdBreak = false;
        this.adBreak.showAds = false;
        if (this.debug) {
          console.warn("[StormcloudVideoPlayer] VMAP ad request failed:", error);
        }
      }
    }
  }

  // ───────────────────────────────────────────────────────────────
  //  Public API
  // ───────────────────────────────────────────────────────────────

  getCurrentAdIndex(): number {
    return this.timing.currentAdIndex;
  }

  getTotalAdsInBreak(): number {
    return this.timing.totalAdsInBreak;
  }

  getAdRemainingMs(): number {
    return this.timing.getRemainingAdMs();
  }

  isAdPlaying(): boolean {
    return this.adBreak.inAdBreak && this.adPlayer.isAdPlaying();
  }

  isShowingAds(): boolean {
    return this.adBreak.showAds;
  }

  getStreamType(): "hls" | "other" {
    const url = this.config.src.toLowerCase();
    if (
      url.includes(".m3u8") ||
      url.includes("/hls/") ||
      url.includes("application/vnd.apple.mpegurl")
    ) {
      return "hls";
    }
    return "other";
  }

  shouldShowNativeControls(): boolean {
    const streamType = this.getStreamType();
    if (streamType === "other") {
      return !(this.config.showCustomControls ?? false);
    }
    return !!(
      this.config.allowNativeHls && !(this.config.showCustomControls ?? false)
    );
  }

  isLive(): boolean {
    return this.hlsEngine.isLiveStream;
  }

  get videoElement(): HTMLVideoElement {
    return this.video;
  }

  toggleMute(): void {
    ctrlToggleMute(this.video, this.adPlayer, this.debug);
  }

  toggleFullscreen(): Promise<void> {
    return ctrlToggleFullscreen(this.video, this.debug);
  }

  isMuted(): boolean {
    return ctrlIsMuted(this.video, this.adPlayer);
  }

  setMuted(muted: boolean): void {
    ctrlSetMuted(this.video, this.adPlayer, muted, this.debug);
  }

  setVolume(volume: number): void {
    ctrlSetVolume(this.video, this.adPlayer, volume, this.debug);
  }

  getVolume(): number {
    return ctrlGetVolume(this.video, this.adPlayer);
  }

  isFullscreen(): boolean {
    return ctrlIsFullscreen();
  }

  resize(): void {
    ctrlResizePlayer(this.video, this.adPlayer, this.debug);
  }

  // ───────────────────────────────────────────────────────────────
  //  Destroy
  // ───────────────────────────────────────────────────────────────

  destroy(): void {
    this.adBreak.stopContinuousFetching();
    this.preloadPool.stop();
    this.timing.destroy();
    this.adBreak.clearPendingAdBreak();
    this.placeholder.destroy();

    if (this.timeUpdateHandler) {
      this.video.removeEventListener("timeupdate", this.timeUpdateHandler);
      delete this.timeUpdateHandler;
    }
    if (this.endedHandler) {
      this.video.removeEventListener("ended", this.endedHandler);
      delete this.endedHandler;
    }
    if (this.emptiedHandler) {
      this.video.removeEventListener("emptied", this.emptiedHandler);
      delete this.emptiedHandler;
    }

    this.adConfig.destroyTracking();
    this.hlsEngine.destroy();
    this.adPlayer?.destroy();

    if (this.palPlaybackStarted) {
      this.palPlaybackStarted = false;
      this.palNonce.sendPlaybackEnd();
    }
    this.palNonce.reset();

    this.adBreak.adRequestQueue = [];
    this.timing.consecutiveFailures = 0;
  }
}
